import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslate } from "../../../../components/TranslationText";
import { fetchMySemenDosesApi } from "../Api/semenOwnershipCertificateApi";
import { DEFAULT_PAGE_SIZE } from "../../../../constants/serviceListConstants";

/**
 * Custom hook managing Semen Ownership Certificate Requests state, server-side fetching, filtering, and pagination.
 * Follows the AllServicesRequestsPage pattern with server-side searchTerm and status filtering.
 */
export const useSemenOwnershipCertificateRequests = () => {
    const { translate } = useTranslate();
    const currentLanguage = localStorage.getItem("eahsLang") ?? "en";

    const [doses, setDoses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("all");

    // Server-side fetch data from API
    const fetchDoses = useCallback(async (page = 1, status = selectedStatus, keyword = searchTerm) => {
        setLoading(true);
        try {
            const cleanKeyword = (keyword || "").trim();
            const response = await fetchMySemenDosesApi({
                pageNumber: page,
                pageNumbers: page,
                pageSize: DEFAULT_PAGE_SIZE,
                status: status === "all" ? "" : status,
                searchTerm: cleanKeyword,
            });

            const list = response?.result ?? [];

            const total = response?.paginationInfo?.totalCount;

            setDoses(list);
            setTotalCount(total);
        } catch (error) {
            console.error("Failed to load semen doses:", error);
            toast.error(translate("web.somethingWentWrong", "Something went wrong"));
            setDoses([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [selectedStatus, searchTerm, translate]);

    // Initial fetch and language change
    useEffect(() => {
        fetchDoses(1, "all", "");
        setCurrentPage(1);
    }, [currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle search change
    const handleSearchChange = (e) => {
        const val = e?.target?.value ?? "";
        setSearchTerm(val);
        if (!val.trim()) {
            setCurrentPage(1);
            fetchDoses(1, selectedStatus, "");
        }
    };

    const handleSearchSubmit = () => {
        const keyword = (searchTerm || "").trim();
        setCurrentPage(1);
        fetchDoses(1, selectedStatus, keyword);
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        setCurrentPage(1);
        fetchDoses(1, selectedStatus, "");
    };

    const handleStatusChange = (status) => {
        setSelectedStatus(status);
        setCurrentPage(1);
        fetchDoses(1, status, searchTerm);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));
    const isLastPage = doses.length < DEFAULT_PAGE_SIZE;

    const goToPage = (newPage) => {
        if (newPage < 1 || (newPage > totalPages && isLastPage)) return;
        setCurrentPage(newPage);
        fetchDoses(newPage, selectedStatus, searchTerm);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const refresh = () => {
        fetchDoses(currentPage, selectedStatus, searchTerm);
    };

    return {
        doses, // Direct server data (no client-side filtering)
        loading,
        currentPage,
        totalPages,
        isLastPage,
        searchTerm,
        selectedStatus,
        handleSearchChange,
        handleSearchSubmit,
        handleClearSearch,
        handleStatusChange,
        goToPage,
        refresh,
    };
};
